// View model for the "Issued bonuses" tab: one row per comp saved in
// bi_backoffice.vip_comp_bonuses_hist, newest first.
import { formatAmount, formatDateTimeUTC, storedAmount } from './format';
import { formatMoney } from './notEligible';
import { bonusBackofficeUrl, formatShortUTC, fridayDate } from './timeline';
import { rateLine } from './resultCardView';
import { splitComment } from './issue';

export const STATUS_FILTERS = [
  { key: 'pending', label: 'Pending' },
  { key: 'pushed', label: 'Pushed' },
  { key: 'issued', label: 'Issued' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: '', label: 'All' },
];

const STATUS = {
  pending: { label: 'Pending', variant: 'warning' },
  pushed: { label: 'Pushed', variant: 'info' },
  issued: { label: 'Issued', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
};

const OVERRIDE_PREFIX = 'Issued despite failed checks';

// ALL_MANAGERS: the manager filter's "everyone" value (a Select item cannot be empty).
export const ALL_MANAGERS = 'all';

// managerOptions: the managers who saved a comp, plus the selected one if the list lacks it.
export const managerOptions = (managers, selected) => {
  const list = managers || [];
  return selected === ALL_MANAGERS || list.includes(selected) ? list : [...list, selected];
};

// managerParam: the created_by query parameter for the chosen manager.
export const managerParam = (manager) => (manager === ALL_MANAGERS ? {} : { created_by: manager });

// periodLabel: what the comp is attached to — the Friday reset or the bonus/cashout that opened
// the period.
export const periodLabel = (row) => (row.period_action === 'friday'
  ? `Friday reset · ${fridayDate(row.period_action_id) || row.period_action_id}`
  : `${row.period_action} #${row.period_action_id} · ${formatShortUTC(row.period_start)}`);

// commentText: the reasons the backend saved first, then "Comment by <manager>: …".
const commentText = (row) => {
  const { reasons, note } = splitComment(row.comment);
  return [...reasons, ...(note ? [`Comment by ${row.created_by}: ${note}`] : [])].join('\n');
};

export const issuedRowView = (row) => {
  const converted = row.eligible_amount && row.eligible_currency && row.eligible_currency !== row.currency;
  const changed = row.updated_by ? `${row.status === 'cancelled' ? 'cancelled' : 'corrected'} by ${row.updated_by} · ${formatDateTimeUTC(row.updated_at)}` : '';
  return {
    id: row.id,
    userId: row.user_id,
    player: row.player_email || row.player_nickname || String(row.user_id),
    added: formatDateTimeUTC(row.created_at),
    addedBy: row.created_by,
    changed,
    amount: formatAmount(storedAmount(row.amount), row.currency),
    title: row.title,
    wagering: `${row.wagering}x`,
    eligible: converted ? `≈ ${formatMoney(row.eligible_amount, row.eligible_currency)}` : '',
    rate: converted ? rateLine(row.exchange_rate, row.eligible_currency, row.currency) : null,
    period: periodLabel(row),
    status: STATUS[row.status] || { label: row.status, variant: 'secondary' },
    override: (row.comment || '').startsWith(OVERRIDE_PREFIX),
    comment: commentText(row),
    canCancel: row.status === 'pending',
    // the back office bonus the issuing job created, once it did
    bonusId: row.issued_bonus_id ?? null,
    bonusUrl: bonusBackofficeUrl(row.issued_bonus_id),
  };
};

// pageText: "1–50 of 120".
export const pageText = ({ page, page_size: size, total }) => (total
  ? `${(page - 1) * size + 1}–${Math.min(page * size, total)} of ${total}`
  : 'No bonuses');
